import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Ticket, TicketAggregate } from 'src/app/models/ticket/ticket.model';
import { getTicketType } from 'src/app/models/ticket/ticket.util';

@Component({
    selector: 'app-ticket-type-chart',
    styleUrls: ['ticket-type-chart.component.scss'],
    templateUrl: './ticket-type-chart.component.html',
    standalone: false
})
export class TicketTypeChartComponent implements OnChanges {
  @Input() tickets: Ticket[];

  @Input() ticketAggregate: TicketAggregate | null = null;

  @Input() isLoading: boolean = false;

  @ViewChild(BaseChartDirective) chart: BaseChartDirective;

  private readonly typeOrder = ['Care', 'Finance', 'Proactive', 'Support'];
  private readonly typeColors: Record<string, string> = {
    Care: '#2a4b98', // dark blue
    Finance: '#2199e8', // light blue
    Proactive: '#ff742e', // orange
    Support: '#ffbb2c', // yellow
  };

  public pieChartData: ChartData<'pie'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Tickets',
        borderWidth: 1,
        hoverBorderWidth: 1,
        backgroundColor: this.typeOrder.map(type => this.typeColors[type]),
      },
    ],
  };

  backgroundColor = [];

  public pieChartType: ChartType = 'pie' as ChartType;

  public pieChartOptions: ChartConfiguration['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        maxWidth: 195,
        display: true,
        position: 'right',
        labels: {
          color: 'white',
          boxHeight: 14,
          boxWidth: 14,
          font: {
            size: 15,
          },
        },
      },
    },
    layout: {
      padding: {
        bottom: 5,
        left: 5,
        right: 5,
      },
    },
  };

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('ticketAggregate' in changes) {
      this.isLoading = true;
      const filtered = this.filterAggregate(this.ticketAggregate);
      const labels = Object.keys(filtered).sort((a, b) => a.localeCompare(b));
      const data = labels.map((k) => filtered[k] ?? 0);

      this.updateChart(labels, data);
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    if ('tickets' in changes) {
      this.isLoading = true;
      const counts = this.countTicketsByType(this.tickets ?? []);
      const labels = Object.keys(counts).sort((a, b) => a.localeCompare(b));
      const data = labels.map((k) => counts[k] ?? 0);

      this.updateChart(labels, data);
      this.isLoading = false;

      this.cdr.detectChanges();
    }
  }

  filterAggregate(aggregate: TicketAggregate | null): Partial<TicketAggregate> {
    if (!aggregate) return {};
    const filtered = Object.entries(aggregate)
      .filter(([_, value]) => value !== 0)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Partial<TicketAggregate>);
    const { total, open, nonProactive, closed, ...rest } = filtered;
    const capitalized: Partial<TicketAggregate> = {};
    Object.keys(rest).forEach((key) => {
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      capitalized[capitalizedKey] = rest[key];
    });
    return capitalized;
  }

  countTicketsByType(tickets: Ticket[]): Record<string, number> {
    const ticketCounts = tickets.reduce<Record<string, number>>(
      (acc, ticket) => {
        const type = getTicketType(ticket)[0];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        if (type.length) acc[type] = (acc[type] ?? 0) + 1;
        return acc;
      },
      {},
    );
    this.chart?.update();
    return this.sortRecordByKey(ticketCounts);
  }

  sortRecordByKey(record: Record<string, number>): Record<string, number> {
    const sortedKeys = Object.keys(record).sort();
    const sortedRecord: Record<string, number> = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const key of sortedKeys) {
      sortedRecord[key] = record[key];
    }
    return sortedRecord;
  }

  updateChart(labels: string[], data: number[]): void {
    const dataByType: Record<string, number> = {};
    labels.forEach((label, i) => {
      dataByType[label] = data[i];
    });
    const chartData = this.typeOrder.map(type => dataByType[type] ?? 0);
    const chartLabels = this.typeOrder;
    const isEmpty = !chartData.some((v) => v > 0);
    this.pieChartData.labels = isEmpty ? [] : chartLabels;
    this.pieChartData.datasets[0].data = isEmpty ? [] : chartData;
    this.pieChartData.datasets[0].backgroundColor = this.typeOrder.map(type => this.typeColors[type]);
    this.chart?.update();
  }
}
